import moment from "moment";
import { useState } from "react";
import { IRow } from "../../components/table/collapse/CollapsibleTable";
import { IProcessedScan, IScanDisplay } from "../../models/stat";
import HBScanStatService from "../../services/stat/HBScanStatService";
import DateUtils from "../../utils/dateUtils";
import IEmployee from "../../models/employee";
import { startOfWeek } from "date-fns";
import { IPropertyGroup } from "../../models/propertyGroup";
import propertyGroupService from "../../services/propertyGroupService/PropertyGroupService";
import userService from "../../services/user/UserService";
import inOutScanOverrideService from "../../services/inOutScanOverride/InOutScanOverride";
import WorkScheduleService from "../../services/workSchedule/WorkScheduleService";
import { IWorkSchedule, IWorkScheduleDay } from "../../models/workSchedule";
import auth from "../../services/auth/AuthService";
import { IOverrideUpdatePayload } from "../../components/table/collapse/InlineTimestampCell";
import companyService from "../../services/company/CompanyService";
import ICompany from "../../models/company";


const EMPTY_CHAR = "-"
const NUM_EMPTY_CHARS = 15
export const EMPTY_CHARS = EMPTY_CHAR.repeat(NUM_EMPTY_CHARS)
export const AUTO_SCHEDULE_AUTHOR = "AUTO";


const getInOutOverrideKey = (userId: number, originalUtc: Date, inOutCode: string) => `${userId}|${moment(originalUtc).toISOString()}|${inOutCode}`;
const getScheduleKey = (userId: number, propertyGroupId: number) => `${userId}|${propertyGroupId}`;

const parseHms = (hms?: string | null) => {
    if (!hms) return null;
    const parsedTime = moment.utc(hms, ["HH:mm:ss", "HH:mm"], true).local();
    if (!parsedTime.isValid()) return null;
    return { h: parsedTime.hour(), m: parsedTime.minute(), s: parsedTime.second() };
};

const combineDateTime = (base: Date, hms?: string | null) => {
    const parts = parseHms(hms);
    if (!parts) return undefined;

    const day = moment(base).clone().local().set({
        hour: parts.h,
        minute: parts.m,
        second: parts.s,
        millisecond: 0,
    });
    return day.toDate();
};

const getPlannedInterval = (
    originalStart: Date,
    day?: IWorkScheduleDay
): { scheduledStart?: Date; scheduledEnd?: Date; isHoliday: boolean } => {
    if (!day) return { scheduledStart: undefined, scheduledEnd: undefined, isHoliday: false };
    if (day.isHoliday) return { scheduledStart: undefined, scheduledEnd: undefined, isHoliday: true };

    const combinedStart = combineDateTime(originalStart, day.startTime);
    const combinedEnd = combineDateTime(originalStart, day.endTime);
    if (!combinedStart || !combinedEnd) return { scheduledStart: undefined, scheduledEnd: undefined, isHoliday: false };

    const isOvernightShift = moment(combinedEnd).isSameOrBefore(moment(combinedStart));
    const adaptedEnd = isOvernightShift ? moment(combinedEnd).add(1, "day").toDate() : combinedEnd;

    return { scheduledStart: combinedStart, scheduledEnd: adaptedEnd, isHoliday: false };
};

const getScheduleDay = (
    schedule: IWorkSchedule | undefined,
    originalStart: Date
) => {
    if (!schedule) return undefined;

    const weekday = moment(originalStart).day();
    return schedule.days.find(d => Number(d.weekday) === weekday);
};

const getOverridenMinutes = (start?: Date, end?: Date, breakMinutes: number = 0) => {
    if (!start || !end) return 0;
    const diff = getDateDiffInMin(start, end);
    return Math.round(Math.max(0, diff - breakMinutes));
};

const normalizeOverrideDisplay = (
    rawStart: Date,
    rawEnd: Date | undefined,
    effStart: Date,
    effEnd: Date | undefined
) => {
    const isEndBeforeStart = !!(effEnd && moment(effEnd).isSameOrBefore(effStart));

    if (!isEndBeforeStart) {
        return {
            overStart: !areDatesEqualByMinute(effStart, rawStart) ? effStart : undefined,
            overEnd: rawEnd && effEnd && !areDatesEqualByMinute(effEnd, rawEnd) ? effEnd : undefined,
        };
    }

    return {
        overStart: !areDatesEqualByMinute(effStart, rawStart) ? effStart : undefined,
        overEnd: rawEnd && !areDatesEqualByMinute(effStart, rawEnd) ? effStart : undefined,
    };
};

const areDatesEqualByMinute = (a?: Date, b?: Date) => !!a && !!b && moment(a).isSame(moment(b), "minutes");
const isDateAfterByMinute = (a: Date, b: Date) => moment(a).isAfter(b, "minutes");
const isDateBeforeByMinute = (a: Date, b: Date) => moment(a).isBefore(b, "minutes");
const getAbsMinuteDiff = (a: Date, b: Date) => Math.abs(moment(a).diff(moment(b), "minutes"));

export const roundDateToNearestMin = (date: Date) => moment(date)
    .add(30, 'seconds')
    .startOf('minute')
    .toDate();

const getDateDiffInMin = (start?: Date, end?: Date) => {
    if (!start || !end) return 0;
    return Math.round(moment(end).diff(moment(start), "minutes"));
};

interface IOverride {
    overridenDate: Date;
    overridenBy: string;
}

const useTimesheetScans = () => {
    const [start, setStart] = useState<Date>(startOfWeek(new Date()));
    const [end, setEnd] = useState<Date | undefined>(new Date());
    const [propertyGroups, setPropertyGroups] = useState<IPropertyGroup[]>([]);
    const [selectablePropertyGroups, setSelectablePropertyGroups] = useState<IPropertyGroup[]>([]);
    const [selectedPropertyGroups, setSelectedPropertyGroups] = useState<number[]>([]);
    const [inOutRows, setInOutRows] = useState<IRow<IScanDisplay>[]>([]);
    const [employees, setEmployees] = useState<IEmployee[]>([]);
    const [selectableEmployees, setSelectableEmployees] = useState<IEmployee[]>([]);
    const [selectedEmployees, setSelectedEmployees] = useState<number[]>([]);
    const [inOutTotal, setInOutTotal] = useState<number>(0);
    const [workedTotal, setWorkedTotal] = useState<number>(0);
    // const [adjustedTotal, setAdjustedTotal] = useState<number>(0);
    const [overridenInOutTotal, setOverridenInOutTotal] = useState<number | undefined>(undefined);
    // const [overridenAdjustedTotal, setOverridenAdjustedTotal] = useState<number | undefined>(undefined);
    const [localScans, setLocalScans] = useState<IProcessedScan[]>([]);
    const [inOutScans, setInOutScans] = useState<IProcessedScan[]>([]);
    const [manualOverridesMap, setManualOverridesMap] = useState<Map<string, IOverride>>(new Map());
    // const [hasAdjustedTime, setHasAdjustedTime] = useState<boolean>(false);
    const [workScheduleMap, setWorkScheduleMap] = useState<Map<string, IWorkSchedule>>(new Map());
    const [isEmployeeMode, setIsEmployeeMode] = useState<boolean>(false);
    const [company, setCompany] = useState<ICompany>();

    const getData = async (isEmployeeMode: boolean) => {
        if (!start || !end) return;

        const company = await companyService.GetCompanyByUser();
        setCompany(company);

        const { localScans, inOutScans } = await getScansByCompany(start, end);
        setLocalScans(localScans);
        setInOutScans(inOutScans);

        const { manualOverridesMap, workScheduleMap } = await getOverridesByCompany(start, end);
        setManualOverridesMap(manualOverridesMap);
        setWorkScheduleMap(workScheduleMap);

        const propertyGroups = await propertyGroupService.GetPropertyGroups()
        const propertyGroupsIdsWithScans = new Set(inOutScans.map((s) => s.start.propertyId))
        const propertyGroupsWithScans = propertyGroups.filter((pg) => propertyGroupsIdsWithScans.has(pg.id)).sort((a, b) => a.name.localeCompare(b.name));

        const employees = (await userService.GetUsersByCompany()).filter(e => !e.isLocked)
        const employeeIdsWithScans = new Set(inOutScans.map((s) => s.start.userId))
        const employeesWithScans = employees.filter((e) => employeeIdsWithScans.has(e.id)).sort((a, b) => a.name.localeCompare(b.name))

        setPropertyGroups(propertyGroupsWithScans);
        setSelectablePropertyGroups(propertyGroupsWithScans);
        setEmployees(employeesWithScans);
        setSelectableEmployees(employeesWithScans);

        switchEmployeeMode(isEmployeeMode, employeesWithScans, propertyGroupsWithScans);
    }

    const switchEmployeeMode = (isEmployeeMode: boolean, employeesWithScans?: IEmployee[], propertyGroupsWithScans?: IPropertyGroup[]) => {
        setIsEmployeeMode(isEmployeeMode);
        if (isEmployeeMode) {
            setSelectedPropertyGroups([]);
            setSelectableEmployees(employeesWithScans || employees);
            setSelectedEmployees([employeesWithScans?.length ? employeesWithScans[0].id : employees[0]?.id || 0]);
        } else {
            setSelectedEmployees([]);
            setSelectablePropertyGroups(propertyGroupsWithScans || propertyGroups);
            setSelectedPropertyGroups([propertyGroupsWithScans?.length ? propertyGroupsWithScans[0].id : propertyGroups[0]?.id || 0]);
        }
    }

    const getOverridesByCompany = async (
        start: Date,
        end: Date,
    ) => {
        if (!start || !end) return { manualOverridesMap: new Map(), workScheduleMap: new Map() };

        const users = await userService.GetUsersByCompany();

        const manualOverrides = await inOutScanOverrideService.GetByCompany(start, end);
        const manualOverridesMap = new Map<string, IOverride>();

        for (const override of manualOverrides) {
            manualOverridesMap.set(getInOutOverrideKey(override.userId, override.originalTimestamp, override.inOutCode), {
                overridenDate: override.overrideTimestamp,
                overridenBy: users.find(u => u.id === override.editedByUserId)?.name || "Unknown"
            });
        }

        const workSchedules = await WorkScheduleService.GetByCompany();
        const workScheduleMap = new Map<string, IWorkSchedule>();

        for (const schedule of workSchedules) {
            workScheduleMap.set(getScheduleKey(schedule.userId, schedule.propertyGroupId), schedule);
        }

        return { manualOverridesMap, workScheduleMap };
    };

    const getScansByCompany = async (
        start: Date,
        end: Date,
    ) => {
        if (!start || !end) return { localScans: [], inOutScans: [] };

        const [localScans, inOutScans] = await Promise.all([
            HBScanStatService.getCompanyScans(start, end),
            HBScanStatService.getInOutScansByCompany(start, end),
        ]);

        return { localScans, inOutScans };
    };

    const updateManualOverride = ({
        userId,
        inOutCode,
        originalTimestamp,
        overrideTimestamp,
    }: IOverrideUpdatePayload) => {
        if (!auth.user || !auth.user.name) return;
        const authorName = auth.user.name;

        let nextMap: Map<string, IOverride> | undefined;
        setManualOverridesMap(prev => {
            const next = new Map(prev);
            const key = getInOutOverrideKey(userId, originalTimestamp, inOutCode);
            if (overrideTimestamp) {
                next.set(key, {
                    overridenDate: overrideTimestamp,
                    overridenBy: authorName
                });
            } else {
                next.delete(key);
            }
            nextMap = next;
            return next;
        });

        if (nextMap) void getTimesheets(isEmployeeMode, nextMap);
    };

    // const getAdjustedMinutes = (minutes: number, propertyId: number) => {
    //     if (minutes < 1) return 0;

    //     const propertyGroup = propertyGroups.find(pg => pg.id === propertyId);
    //     if (!propertyGroup) return minutes;

    //     let adjustedMinutes = minutes;
    //     if (propertyGroup.timeRoundingSettings?.isEnabled) {
    //         adjustedMinutes = getRoundedMinutes(adjustedMinutes, propertyGroup.timeRoundingSettings);
    //     }
    //     if (propertyGroup.isBreakDeductionEnabled) {
    //         adjustedMinutes = getBreakDeductedMinutes(adjustedMinutes, propertyGroup.employeeBreakRules);
    //     }

    //     return Math.round(adjustedMinutes);
    // }

    // const getBreakDeductedMinutes = (minutes: number, employeeBreakRules: IEmployeeBreakRule[]) => {
    //     if (minutes < 1) return 0;
    //     if (!employeeBreakRules || employeeBreakRules.length === 0) return minutes;

    //     const workedHours = minutes / 60;
    //     // Ex : if employee worked 7hs and there are 2 rules : one for 4hs and one for 6hs, the rule for 6hs will be applied
    //     const applicableRule = employeeBreakRules
    //         .filter(rule => rule.minHoursWorked <= workedHours)
    //         .sort((a, b) => b.minHoursWorked - a.minHoursWorked)[0];
    //     if (!applicableRule) return minutes;

    //     const { minHoursWorked, totalBreakMinutes } = applicableRule;
    //     if (workedHours < minHoursWorked) return minutes;

    //     const breakDeductedMinutes = minutes - totalBreakMinutes;
    //     if (breakDeductedMinutes < 1) return 0;

    //     return breakDeductedMinutes;
    // };

    // const getRoundedMinutes = (minutes: number, roundingSettings?: ITimeRoundingSettings) => {
    //     if (minutes < 1) return 0;
    //     if (!roundingSettings || !roundingSettings.isEnabled) return minutes;

    //     const { direction, interval } = roundingSettings;
    //     if (interval < 1) return minutes;

    //     const roundingMap = new Map<RoundingDirection, number>([
    //         [RoundingDirection.UP, Math.ceil(minutes / interval) * interval],
    //         [RoundingDirection.DOWN, Math.floor(minutes / interval) * interval],
    //         [RoundingDirection.NEAREST, Math.round(minutes / interval) * interval]
    //     ]);

    //     const roundedMinutes = roundingMap.get(direction);
    //     if (!roundedMinutes) return minutes;
    //     if (roundedMinutes < 1) return 0;

    //     return roundedMinutes;
    // }

    const handleOverrides = (
        originalStart: Date,
        originalEnd: Date | undefined,
        userId: number,
        localCode: string,
        propertyId: number,
        originalMinutes: number,
        originalProductivity: number,
        workedMinutes: number,
        accumulatedOverridenMinutes: number,
        accumulatedOverridenProductivity: number,
        overrideMap: Map<string, IOverride>,
    ) => {
        let hasOverridenTime = false, employeeHasOverridenTime = false, shiftHasOverridenTime = false;

        const roundedStart = roundDateToNearestMin(originalStart);
        const roundedEnd = originalEnd ? roundDateToNearestMin(originalEnd) : undefined;

        const manualStart = overrideMap.get(getInOutOverrideKey(userId, originalStart, localCode));
        const manualEnd = originalEnd ? overrideMap.get(getInOutOverrideKey(userId, originalEnd, localCode)) : undefined;
        if (manualStart || manualEnd) {
            hasOverridenTime = employeeHasOverridenTime = shiftHasOverridenTime = true;
        }

        // Priority 1: manual overrides (if present)
        let effStart = manualStart?.overridenDate ?? roundedStart;
        let effEnd = roundedEnd ? (manualEnd?.overridenDate ?? roundedEnd) : undefined;

        let isStartLate = false;
        let isEndEarly = false;
        let breakMinutes = 0;

        // Priority 2: schedule clamp (and compute lateness/earliness from RAW vs schedule)
        const schedule = workScheduleMap.get(getScheduleKey(userId, propertyId));
        if (schedule) {
            const day = getScheduleDay(schedule, roundedStart);
            breakMinutes = day?.breakMinutes || 0;
            if (roundedEnd && breakMinutes > 0 && originalMinutes > 0) hasOverridenTime = employeeHasOverridenTime = shiftHasOverridenTime = true;

            const { scheduledStart, scheduledEnd, isHoliday } = getPlannedInterval(roundedStart, day);

            if (!isHoliday) {
                const outsideTol = company?.outsideScheduleTolerance ?? 0; // arrived early / left late
                const insideTol = company?.insideScheduleTolerance ?? 0;   // arrived late / left early

                if (scheduledStart) {
                    isStartLate = isDateAfterByMinute(roundedStart, scheduledStart);

                    // Only auto-adjust if there is no manual override for this endpoint
                    if (!manualStart) {
                        const delta = getAbsMinuteDiff(roundedStart, scheduledStart);

                        // Case A: arrived EARLY (before scheduled start) → compare with outsideTol
                        if (isDateBeforeByMinute(roundedStart, scheduledStart)) {
                            if (delta <= outsideTol) {
                                effStart = scheduledStart;
                                hasOverridenTime = employeeHasOverridenTime = shiftHasOverridenTime = true;
                            }
                        }
                        // Case B: arrived LATE (after scheduled start) → compare with insideTol
                        if (isDateAfterByMinute(roundedStart, scheduledStart)) {
                            if (delta <= insideTol) {
                                effStart = scheduledStart;
                                hasOverridenTime = employeeHasOverridenTime = shiftHasOverridenTime = true;
                            }
                        }
                    }
                }

                if (roundedEnd && scheduledEnd) {
                    isEndEarly = isDateBeforeByMinute(roundedEnd, scheduledEnd);

                    if (!manualEnd) {
                        const delta = getAbsMinuteDiff(roundedEnd, scheduledEnd);

                        // Case C: left LATE (after scheduled end) → compare with outsideTol
                        if (isDateAfterByMinute(roundedEnd, scheduledEnd)) {
                            if (delta <= outsideTol) {
                                effEnd = scheduledEnd;
                                hasOverridenTime = employeeHasOverridenTime = shiftHasOverridenTime = true;
                            }
                        }
                        // Case D: left EARLY (before scheduled end) → compare with insideTol
                        if (isDateBeforeByMinute(roundedEnd, scheduledEnd)) {
                            if (delta <= insideTol) {
                                effEnd = scheduledEnd;
                                hasOverridenTime = employeeHasOverridenTime = shiftHasOverridenTime = true;
                            }
                        }
                    }
                }
            }
        }

        const { overStart, overEnd } = normalizeOverrideDisplay(
            roundedStart,
            roundedEnd,
            effStart,
            effEnd
        );

        const overStartAuthor =
            overStart ? (manualStart ? manualStart.overridenBy : AUTO_SCHEDULE_AUTHOR) : undefined;
        const overEndAuthor =
            overEnd ? (manualEnd ? manualEnd.overridenBy : AUTO_SCHEDULE_AUTHOR) : undefined;

        let overridenMinutes = 0;

        if (shiftHasOverridenTime) {
            overridenMinutes = getOverridenMinutes(overStart || roundedStart, overEnd || roundedEnd, breakMinutes);
            if (overridenMinutes !== originalMinutes && overridenMinutes > 0) {
                hasOverridenTime = employeeHasOverridenTime = shiftHasOverridenTime = true;
            }
        }

        accumulatedOverridenMinutes += shiftHasOverridenTime ? overridenMinutes : originalMinutes;

        const overridenProductivity = getProductivity(workedMinutes, overridenMinutes);
        accumulatedOverridenProductivity += shiftHasOverridenTime ? overridenProductivity || 0 : originalProductivity;

        // const overridenAdjustedMinutes = getAdjustedMinutes(overridenMinutes, subrow.start.propertyId);
        // accumulatedOverridenAdjustedMinutes += overridenAdjustedMinutes;

        // const overridenProductivity = getProductivity(
        //     workedMinutes,
        //     overridenAdjustedMinutes || overridenMinutes
        // );

        return {
            overStart,
            overEnd,
            showStartAuthor: overStartAuthor,
            showEndAuthor: overEndAuthor,
            overridenMinutes,
            overridenProductivity,
            hasOverridenTime,
            employeeHasOverridenTime,
            shiftHasOverridenTime,
            accumulatedOverridenMinutes,
            accumulatedOverridenProductivity,
            isStartLate,
            isEndEarly,
        };
    };

    const getWorkedMinutes = (
        inDate: Date,
        outDate: Date | undefined,
        inOutUserId: number,
        sortedInOutScans: IProcessedScan[],
        currentIndex: number,
        localScans: IProcessedScan[]
    ) => {
        const sumMinutes = (scans: IProcessedScan[]) =>
            scans.reduce((total, scan) => total + (Math.round(scan.minutes)), 0);

        const nextSubrow = sortedInOutScans[currentIndex - 1];
        let nextInDate = nextSubrow && nextSubrow.start.date;

        let filteredScans = localScans.filter(
            (scan) =>
                scan.start.userId === inOutUserId &&
                moment(scan.start.date).isBetween(
                    inDate,
                    outDate || nextInDate,
                    undefined,
                    "[]"
                )
        );

        let workedMinutes = sumMinutes(filteredScans);

        if (!outDate && !nextSubrow) {
            filteredScans = localScans.filter(
                (scan) =>
                    scan.start.userId === inOutUserId &&
                    moment(scan.start.date).isSameOrAfter(inDate)
            );
            workedMinutes = sumMinutes(filteredScans);
        }

        return Math.round(workedMinutes);
    };

    const getProductivity = (workedMinutes: number, inOutMinutes: number) => {
        if (!inOutMinutes) return undefined;

        return Math.min(
            100,
            Math.max(0, Math.round((workedMinutes / inOutMinutes) * 100) || 0)
        );
    };

    const filterByEmployees = (scans: IProcessedScan[], employeeIdSet?: Set<number>) => {
        if (!employeeIdSet) return scans;
        return scans.filter(scan => employeeIdSet.has(scan.start.userId));
    };

    const filterByPropertyGroups = (scans: IProcessedScan[], propertyGroupIdSet?: Set<number>) => {
        if (!propertyGroupIdSet) return scans;
        return scans.filter(scan => propertyGroupIdSet.has(scan.start.propertyId));
    }

    const filterScans = (isEmployeeMode: boolean) => {
        let filteredLocalScans: IProcessedScan[] = [];
        let filteredInOutScans: IProcessedScan[] = [];

        const selectedEmployeeIdsSet = selectedEmployees.length ? new Set(selectedEmployees) : undefined;
        const selectedPropertyGroupIdsSet = selectedPropertyGroups.length ? new Set(selectedPropertyGroups) : undefined;

        if (isEmployeeMode) {
            if (!selectedEmployeeIdsSet) return { filteredLocalScans: [], filteredInOutScans: [] };

            // First: partial filters by employees
            const employeeFilteredInOutScans = filterByEmployees(inOutScans, selectedEmployeeIdsSet);
            const employeeFilteredLocalScans = filterByEmployees(localScans, selectedEmployeeIdsSet);

            // Full filters (only apply property group if selected)
            filteredInOutScans = filterByPropertyGroups(employeeFilteredInOutScans, selectedPropertyGroupIdsSet);
            filteredLocalScans = filterByPropertyGroups(employeeFilteredLocalScans, selectedPropertyGroupIdsSet);

            // For the dropdown: get property groups from partially filtered scans
            const propertyGroupIds = new Set(employeeFilteredInOutScans.map(scan => scan.start.propertyId))
            const propertyGroupsWithScans = propertyGroups
                .filter(pg => propertyGroupIds.has(pg.id))
                .sort((a, b) => a.name.localeCompare(b.name));

            setSelectablePropertyGroups(propertyGroupsWithScans);
        } else {
            if (!selectedPropertyGroupIdsSet) return { filteredLocalScans: [], filteredInOutScans: [] };

            // First: partial filters by property groups
            const propertyFilteredInOutScans = filterByPropertyGroups(inOutScans, selectedPropertyGroupIdsSet);
            const propertyFilteredLocalScans = filterByPropertyGroups(localScans, selectedPropertyGroupIdsSet);

            // Full filters (only apply employee if selected)
            filteredInOutScans = filterByEmployees(propertyFilteredInOutScans, selectedEmployeeIdsSet);
            filteredLocalScans = filterByEmployees(propertyFilteredLocalScans, selectedEmployeeIdsSet);

            // For the dropdown: get employees from partially filtered scans
            const employeeIds = new Set(propertyFilteredInOutScans.map(scan => scan.start.userId))
            const employeesWithScans = employees
                .filter(emp => employeeIds.has(emp.id))
                .sort((a, b) => a.name.localeCompare(b.name));

            setSelectableEmployees(employeesWithScans);
        }

        return { filteredLocalScans, filteredInOutScans };
    }

    const resetTimesheetData = () => {
        setInOutRows([]);
        setInOutTotal(0);
        setWorkedTotal(0);
        // setAdjustedTotal(0);
        // setHasAdjustedTime(false);
    }

    const getTimesheets = async (
        isEmployeeMode: boolean = false,
        overrideMap?: Map<string, IOverride>,
    ) => {
        if (!start || !end) return;
        const effectiveOverrideMap = overrideMap || manualOverridesMap;

        const { filteredLocalScans, filteredInOutScans } = filterScans(isEmployeeMode);
        if (filteredInOutScans.length === 0) return resetTimesheetData();
        let totalMinutes = 0;
        // let totalAdjustedMinutes = 0;
        let totalWorkedHours = 0;

        let hasOverridenTime = false;
        let totalOverridenMinutes = 0;
        // let totalOverridenAdjustedMinutes = 0;

        const usedPropertyGroups = selectedPropertyGroups.length ? selectablePropertyGroups.filter(pg => selectedPropertyGroups.includes(pg.id)) : selectablePropertyGroups;
        if (usedPropertyGroups.length === 0) return resetTimesheetData();

        // setHasAdjustedTime(usedPropertyGroups.some(pg => pg.isBreakDeductionEnabled || pg.timeRoundingSettings?.isEnabled));

        const employeeInOuts = filteredInOutScans.reduce((acc, scan) => {
            if (acc.has(scan.start.userId)) {
                (acc.get(scan.start.userId) as IProcessedScan[]).push(scan);
                return acc;
            }
            return acc.set(scan.start.userId, [scan]);
        }, new Map<number, IProcessedScan[]>());

        const rows: IRow<IScanDisplay>[] = Array.from(
            employeeInOuts.entries()
        ).map(([userId, userScans]) => {
            let employeeHasOverridenTime = false;
            const accumulatedMinutes = userScans.reduce((acc, scan) => {
                const roundedStart = roundDateToNearestMin(scan.start.date);
                const roundedEnd = scan.end ? roundDateToNearestMin(scan.end.date) : undefined;
                const minutes = getDateDiffInMin(roundedStart, roundedEnd);

                acc += minutes;
                return acc;
            }, 0);
            // let accumulatedAdjustedMinutes = 0;
            let accumulatedWorkedMinutes = 0;
            let accumulatedProductivity = 0;

            let accumulatedOverridenMinutes = 0;
            // let accumulatedOverridenAdjustedMinutes = 0;
            let accumulatedOverridenProductivity = 0;

            const sortedScans = userScans.sort(
                (a, b) => moment(b.start.date).unix() - moment(a.start.date).unix()
            );

            const subRows = sortedScans.map((subrow, index, array) => {
                const startDate = subrow.start.date;
                const endDate = subrow.end ? subrow.end.date : undefined;
                const roundedStart = roundDateToNearestMin(startDate);
                const roundedEnd = endDate ? roundDateToNearestMin(endDate) : undefined;
                const minutes = getDateDiffInMin(roundedStart, roundedEnd);
                // const adjustedMinutes = getAdjustedMinutes(minutes, subrow.start.propertyId);
                // accumulatedAdjustedMinutes += adjustedMinutes;

                const workedMinutes = getWorkedMinutes(
                    startDate,
                    endDate,
                    subrow.start.userId,
                    array,
                    index,
                    filteredLocalScans
                );
                accumulatedWorkedMinutes += workedMinutes;

                // const productivity = getProductivity(
                //     workedMinutes,
                //     adjustedMinutes || minutes
                // );
                const productivity = getProductivity(workedMinutes, minutes) || 0;
                accumulatedProductivity += productivity;

                const { overStart, overEnd, showStartAuthor, showEndAuthor, overridenMinutes, overridenProductivity, hasOverridenTime: _hasOverridenTime, employeeHasOverridenTime: _employeeHasOverridenTime, shiftHasOverridenTime, accumulatedOverridenMinutes: _accumulatedOverridenMinutes, accumulatedOverridenProductivity: _accumulatedOverridenProductivity, isStartLate, isEndEarly } = handleOverrides(
                    startDate,
                    endDate,
                    subrow.start.userId,
                    subrow.start.localCode,
                    subrow.start.propertyId,
                    minutes,
                    productivity,
                    workedMinutes,
                    accumulatedOverridenMinutes,
                    accumulatedOverridenProductivity,
                    effectiveOverrideMap,
                );

                if (_hasOverridenTime) hasOverridenTime = true;
                if (_employeeHasOverridenTime) employeeHasOverridenTime = true;

                accumulatedOverridenMinutes = _accumulatedOverridenMinutes;
                accumulatedOverridenProductivity = _accumulatedOverridenProductivity;

                return {
                    ...subrow.start,
                    minutes: DateUtils.displayMinutes(minutes),
                    // adjustedMinutes: DateUtils.displayMinutes(adjustedMinutes),
                    workedMinutes: DateUtils.displayMinutes(workedMinutes),
                    productivity: productivity ? `${productivity}%` : EMPTY_CHARS,
                    date: startDate,
                    endDate: endDate,
                    isEndAutoGenerated: subrow.isAutoGenerated,

                    overridenStart: overStart,
                    overridenEnd: overEnd,
                    overridenStartAuthor: showStartAuthor,
                    overridenEndAuthor: showEndAuthor,
                    overridenMinutes: shiftHasOverridenTime ? DateUtils.displayMinutes(overridenMinutes) : undefined,
                    // overridenAdjustedMinutes: shiftHasOverridenTime ? DateUtils.displayMinutes(overridenAdjustedMinutes) : undefined,
                    overridenProductivity: shiftHasOverridenTime && overridenProductivity ? `${overridenProductivity}%` : shiftHasOverridenTime && productivity ? EMPTY_CHARS : undefined,
                    isStartLate,
                    isEndEarly,
                };
            });

            totalMinutes += accumulatedMinutes;
            // totalAdjustedMinutes += accumulatedAdjustedMinutes;
            totalWorkedHours += accumulatedWorkedMinutes;

            totalOverridenMinutes += employeeHasOverridenTime ? accumulatedOverridenMinutes : accumulatedMinutes;
            // totalOverridenAdjustedMinutes += employeeHasOverridenTime ? accumulatedOverridenAdjustedMinutes : accumulatedAdjustedMinutes;

            const nValidProductivityRows = subRows.filter((r) => r.productivity !== EMPTY_CHARS).length;
            const nValidOverridenProductivityRows = subRows.filter((r) => r.overridenProductivity !== EMPTY_CHARS && r.productivity !== EMPTY_CHARS).length;

            return {
                id: userId,
                subRowData: subRows,
                data: {
                    ...userScans[0].start,
                    minutes: DateUtils.displayMinutes(accumulatedMinutes),
                    // adjustedMinutes: DateUtils.displayMinutes(accumulatedAdjustedMinutes),
                    workedMinutes: DateUtils.displayMinutes(accumulatedWorkedMinutes),
                    productivity: nValidProductivityRows ? `${Math.round(accumulatedProductivity / nValidProductivityRows)}%` : EMPTY_CHARS,
                    // overridenAdjustedMinutes: employeeHasOverridenTime ? DateUtils.displayMinutes(accumulatedOverridenAdjustedMinutes) : undefined,
                    overridenMinutes: employeeHasOverridenTime ? DateUtils.displayMinutes(accumulatedOverridenMinutes) : undefined,
                    overridenProductivity: employeeHasOverridenTime && nValidOverridenProductivityRows ? `${Math.round(accumulatedOverridenProductivity / nValidOverridenProductivityRows)}%` : undefined,
                },
            };
        });

        setInOutRows(rows.sort((a, b) => a.data.username.localeCompare(b.data.username)));
        setInOutTotal(totalMinutes);
        setWorkedTotal(totalWorkedHours);
        // setAdjustedTotal(totalAdjustedMinutes);
        setOverridenInOutTotal(hasOverridenTime ? totalOverridenMinutes : undefined);
        // setOverridenAdjustedTotal(hasOverridenTime ? totalOverridenAdjustedMinutes : undefined);
    };

    return {
        start,
        setStart,
        end,
        setEnd,
        selectablePropertyGroups,
        selectedPropertyGroups,
        setSelectedPropertyGroups,
        inOutRows,
        inOutTotal,
        // adjustedTotal,
        workedTotal,
        overridenInOutTotal,
        // overridenAdjustedTotal,
        getTimesheets,
        selectableEmployees,
        setSelectedEmployees,
        selectedEmployees,
        getData,
        switchEmployeeMode,
        updateManualOverride,
        // hasAdjustedTime,
    };
};

export default useTimesheetScans;
